import type { ReactNode } from 'react';
import { ApiError, NetworkError, isRetryable } from '../api/problem';
import { useI18n } from '../i18n/I18nProvider';
import { Button, Notice, Spinner, type NoticeTone } from './primitives';

export interface EmptyStateProps {
  icon?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  tone?: 'neutral' | 'warning';
}

export function EmptyState({ icon, title, body, action, tone = 'neutral' }: EmptyStateProps) {
  return (
    <div className="dg-empty" role={tone === 'warning' ? 'alert' : undefined}>
      {icon && <span className="dg-empty__icon" aria-hidden="true">{icon}</span>}
      <p className="dg-empty__title">{title}</p>
      {body && <p>{body}</p>}
      {action}
    </div>
  );
}

export function LoadingState() {
  const { t } = useI18n();
  return (
    <div className="dg-empty">
      <Spinner label={t('state.loading')} />
    </div>
  );
}

/**
 * Renders a failed request.
 *
 * The API writes its problem titles and details in plain Arabic and says what to do next (NFR-USE-05), so
 * those are shown as they arrive. The client supplies its own wording only for a `NetworkError`, where the
 * request never reached a server that could have written one.
 *
 * The retry button appears only where asking again could help (`isRetryable`): under a refusal the problem's
 * own detail says what to do instead.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useI18n();
  const { title, body, reference, tone } = describeError(error, t);
  const technical = technicalDetails(error);

  return (
    <Notice tone={tone} title={title} reference={reference}>
      <div className="dg-stack dg-stack--tight">
        <span>{body}</span>
        {technical && (
          // Development and staging only: the API sends `exception` nowhere else (decision 2026-10-14 #1).
          <details className="dg-technical">
            <summary>{t('error.technical')}</summary>
            <pre dir="ltr">{technical}</pre>
          </details>
        )}
        {onRetry && isRetryable(error) && (
          <div>
            <Button variant="secondary" onClick={onRetry}>{t('error.retry')}</Button>
          </div>
        )}
      </div>
    </Notice>
  );
}

export function describeError(
  error: unknown,
  t: (key: string, params?: Record<string, string | number>) => string,
): { title: string; body: string; reference: string | null; tone: NoticeTone } {
  if (error instanceof NetworkError) {
    return { title: t('state.offline'), body: t('error.network'), reference: null, tone: 'warning' };
  }

  if (error instanceof ApiError) {
    const reference = typeof error.problem.traceId === 'string' ? error.problem.traceId : null;
    return {
      title: error.problem.title ?? t('error.title'),
      body: error.problem.detail ?? t('error.unexpected'),
      reference: reference ? t('error.reference', { reference }) : null,
      // A 4xx is something the user can act on; a 5xx is ours, and reads as a failure.
      tone: error.status >= 500 ? 'danger' : 'warning',
    };
  }

  return { title: t('error.title'), body: t('error.unexpected'), reference: null, tone: 'danger' };
}

interface ExceptionDetails {
  type?: string;
  message?: string;
  stackTrace?: string | null;
  inner?: ExceptionDetails;
}

/**
 * The exception a 500 carries where the API exposes it to developers and testers, as text to copy into a bug
 * report; null otherwise, which is always in production.
 */
export function technicalDetails(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const exception = error.problem.exception as ExceptionDetails | undefined;
  if (!exception || typeof exception !== 'object') return null;

  const lines: string[] = [];
  for (let current: ExceptionDetails | undefined = exception; current; current = current.inner) {
    lines.push(`${current.type ?? 'Exception'}: ${current.message ?? ''}`);
    if (current.stackTrace) lines.push(current.stackTrace);
  }
  return lines.join('\n');
}

/**
 * Marks a screen whose API has not shipped yet.
 *
 * Naming the requirement is the point: it tells a reviewer that the screen is accounted for and where to
 * look, rather than leaving a blank page that could equally be a bug.
 */
export function PendingFeature({ requirement, title }: { requirement: string; title?: ReactNode }) {
  const { t } = useI18n();
  return (
    <EmptyState
      icon="🚧"
      title={title ?? t('pending.title')}
      body={t('pending.body', { requirement })}
    />
  );
}

/**
 * The stripe shown above any screen rendering sample data.
 *
 * It renders only when the app was built with `VITE_ENABLE_FIXTURES`, and it is deliberately ugly: nobody
 * should be able to screenshot a fixture screen and mistake it for a real nursery's records.
 */
export function FixtureBanner() {
  const { t } = useI18n();
  if (!import.meta.env.VITE_ENABLE_FIXTURES) return null;
  return <p className="dg-fixture-banner">⚠ {t('fixtures.banner')}</p>;
}
